import React, { useState } from 'react';
import { Container } from 'reactstrap';
import Navbar from '../Navbar';
import Footer from '../Footer';
import GuestChatWidget from '../GuestChatWidget';
import '../landing.scss';
import SeoHead from '../../../../Components/Common/SeoHead';
import { MARKETS, calculateFees, FeeCalcOpts } from './feeRates';

const APP_URL = process.env.REACT_APP_APP_URL || '';

interface ProfitResult {
  ebayFees: number;
  regulatoryFee: number;
  vatAmount: number;
  amazonCost: number;
  grossProfit: number;
  netProfit: number;
  profitMargin: number;
  breakEven: number;
}

const FAQ_ITEMS = [
  {
    q: 'How do I calculate dropshipping profit from Amazon to eBay?',
    a: 'Take your eBay sale price and subtract your Amazon cost, any Amazon postage you pay, and all eBay fees (the Final Value Fee plus the fixed order fee). What remains is your net profit. This calculator does the maths automatically for the UK, US, Germany and Australia.',
  },
  {
    q: 'What is a good profit margin for eBay dropshipping?',
    a: 'Most Amazon to eBay dropshippers aim for a net margin of 10–20% after fees. Thin margins are common in this model, so even small pricing mistakes can turn a sale into a loss. Use the break-even figure to see the minimum price you must charge to avoid losing money.',
  },
  {
    q: 'Does this include eBay and Amazon fees?',
    a: 'It includes eBay’s Final Value Fee and fixed order fee for the selected marketplace. Your Amazon cost is whatever you pay the supplier, including any Amazon postage you enter. It does not include optional extras such as eBay Promoted Listings or currency conversion fees.',
  },
  {
    q: 'What is the break-even sale price?',
    a: 'The break-even price is the lowest eBay sale price at which your net profit is exactly zero — covering your Amazon cost plus all eBay fees. Price anything below it and you lose money on the order. Always list above break-even to stay profitable.',
  },
  {
    q: 'Can Coderom automate this for every product?',
    a: 'Yes. Coderom monitors Amazon prices and stock 24/7 and reprices your eBay listings automatically to protect the margin you set. Instead of checking each product by hand, you define your rules once and let the platform keep every listing profitable.',
  },
];

const ProfitCalculator: React.FC = () => {
  const [marketCode, setMarketCode] = useState('UK');
  const [amazonCost, setAmazonCost] = useState('');
  const [ebayPriceInput, setEbayPriceInput] = useState('');
  const [amazonShipping, setAmazonShipping] = useState('');
  const [result, setResult] = useState<ProfitResult | null>(null);

  // New UI fields — parity with EbayFeeCalculator
  const [sellerType, setSellerType] = useState<'private' | 'business'>('business');
  const [category, setCategory] = useState<string>('most');
  const isVatRegistered = false; // VAT folded into VAT-inclusive FVF rates (no separate VAT)
  const [isTopRatedPlus, setIsTopRatedPlus] = useState<boolean>(false);
  const [isInternational, setIsInternational] = useState<boolean>(false);

  const selectedMarket = MARKETS.find((m) => m.code === marketCode);
  const market = selectedMarket || MARKETS[0];

  const costNum = parseFloat(amazonCost);
  const priceNum = parseFloat(ebayPriceInput);
  const canCalculate =
    amazonCost !== '' && costNum > 0 && ebayPriceInput !== '' && priceNum > 0;

  const numericChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      if (/^\d*\.?\d*$/.test(v)) {
        setter(v);
        setResult(null); // clear stale result on any input change
      }
    };

  const handleMarketChange = (code: string) => {
    setMarketCode(code);
    setResult(null);
  };

  const handleCalculate = () => {
    if (!canCalculate) return;

    const ebayPrice = priceNum;
    const amazonShip = parseFloat(amazonShipping) || 0; // optional, default 0
    const opts: FeeCalcOpts = {
      category,
      sellerType,
      isVatRegistered,
      isTopRatedPlus,
    };
    const ebayFees = calculateFees(ebayPrice, market, opts);
    // International fee (component-level, not in calculateFees — parity with EbayFeeCalculator)
    const intlFee = isInternational
      ? parseFloat((ebayPrice * 0.0165).toFixed(2))
      : 0;
    const totalEbayFees = parseFloat((ebayFees.totalFee + intlFee).toFixed(2));
    const totalCost = costNum + amazonShip;
    const grossProfit = ebayPrice - totalCost;
    const netProfit = ebayPrice - totalCost - totalEbayFees;
    const profitMargin = ebayPrice > 0 ? (netProfit / ebayPrice) * 100 : 0;
    // Guard: denominator (1 - fvf/100) is always > 0 for real market rates (fvf << 100).
    // The numerator guard mirrors the agreed formula.
    const breakEven =
      totalCost + market.fixedFee > 0
        ? (totalCost + market.fixedFee) / (1 - market.fvfPercent / 100)
        : 0;

    setResult({
      ebayFees: totalEbayFees,
      regulatoryFee: ebayFees.regulatoryFee,
      vatAmount: ebayFees.vatAmount,
      amazonCost: totalCost,
      grossProfit: parseFloat(grossProfit.toFixed(2)),
      netProfit: parseFloat(netProfit.toFixed(2)),
      profitMargin: parseFloat(profitMargin.toFixed(1)),
      breakEven: parseFloat(breakEven.toFixed(2)),
    });
  };

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="Amazon to eBay Profit Calculator 2026 | Coderom"
        description="Calculate your exact profit when dropshipping from Amazon to eBay. Enter Amazon cost, eBay sale price and see net profit after all fees instantly. Free tool by Coderom."
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQ_ITEMS.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        }) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.coderom.com/" },
            { "@type": "ListItem", "position": 2, "name": "Profit Calculator", "item": "https://www.coderom.com/tools/profit-calculator" },
          ],
        }) }}
      />
      <Navbar />

      <div className="tools-page">
        <div className="calc-hero">
            <div className="calc-hero-bg" />
            <div className="calc-hero-content">
              <div className="hero-breadcrumb">
                <a href="/">Home</a>
                <span>›</span>
                <span>Tools</span>
                <span>›</span>
                <span>Profit Calculator</span>
              </div>
              <div className="hero-eyebrow">
                <i className="ri-funds-line" aria-hidden="true"></i>
                Free Tool
              </div>
              <h1>Amazon→eBay <span>Profit</span> Calculator 2026</h1>
              <p>Calculate your exact dropshipping profit after all eBay fees — supports UK, US, Germany and Australia.</p>
              <div className="hero-badges">
                <span className="hero-badge">
                  <i className="ri-check-line" aria-hidden="true"></i>
                  Real fee rates
                </span>
                <span className="hero-badge">
                  <i className="ri-check-line" aria-hidden="true"></i>
                  Break-even price
                </span>
                <span className="hero-badge">
                  <i className="ri-check-line" aria-hidden="true"></i>
                  Free tool
                </span>
              </div>
            </div>
          </div>

        <div className="calc-wrapper">
          {/* Market tabs */}
          <div className="market-tabs">
            {MARKETS.map(m => (
              <button
                key={m.code}
                className={`market-tab${selectedMarket?.code === m.code ? ' active' : ''}`}
                onClick={() => handleMarketChange(m.code)}
              >
                <img
                  src={m.flagSrc}
                  alt={m.code}
                  style={{ width: 20, height: 15, borderRadius: 2, objectFit: 'cover' }}
                />
                <span>{m.code === 'UK' ? 'United Kingdom'
                  : m.code === 'US' ? 'United States'
                  : m.code === 'DE' ? 'Germany'
                  : 'Australia'}</span>
              </button>
            ))}
          </div>

          <div className="calc-card">
            {/* Seller settings */}
            <div className="section-label">Seller settings</div>
            <div className="form-grid-2">
              <div className="form-field">
                <label>Seller type</label>
                <div className="seller-toggle">
                  <button
                    className={`seller-btn${sellerType === 'private' ? ' active' : ''}`}
                    onClick={() => { setSellerType('private'); setResult(null); }}
                  >Private</button>
                  <button
                    className={`seller-btn${sellerType === 'business' ? ' active' : ''}`}
                    onClick={() => { setSellerType('business'); setResult(null); }}
                  >Business</button>
                </div>
              </div>
              <div className="form-field">
                <label>Item category</label>
                <div className="calc-input-wrap">
                  <select
                    value={category}
                    onChange={e => { setCategory(e.target.value); setResult(null); }}
                  >
                    <option value="most">Most categories</option>
                    <option value="electronics">Electronics &amp; Computers</option>
                    <option value="clothing">Clothing &amp; Shoes</option>
                    <option value="home">Home &amp; Garden</option>
                    <option value="toys">Toys &amp; Games</option>
                    <option value="health">Health &amp; Beauty</option>
                    <option value="sports">Sports &amp; Leisure</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="vat-row">
              <input type="checkbox" id="pcTopRatedCheck"
                checked={isTopRatedPlus}
                onChange={e => { setIsTopRatedPlus(e.target.checked); setResult(null); }}
                style={{ width: 16, height: 16, accentColor: '#f97316' }} />
              <label htmlFor="pcTopRatedCheck">
                Top Rated Plus seller — 10% discount on final value fee
              </label>
            </div>
            <div className="vat-row">
              <input type="checkbox" id="pcIntlCheck"
                checked={isInternational}
                onChange={e => { setIsInternational(e.target.checked); setResult(null); }}
                style={{ width: 16, height: 16, accentColor: '#f97316' }} />
              <label htmlFor="pcIntlCheck">
                International payment — additional 1.65% fee applies
              </label>
            </div>

            <div className="divider" />

            {/* Pricing */}
            <div className="section-label">Pricing</div>
            <div className="form-grid-3">
              <div className="form-field">
                <label>Amazon cost</label>
                <div className="calc-input-wrap">
                  <span className="calc-input-affix">{selectedMarket?.currencySymbol}</span>
                  <input type="text" inputMode="decimal" placeholder="0.00"
                    value={amazonCost} onChange={numericChange(setAmazonCost)} autoComplete="off" />
                </div>
                <div className="field-note">supplier price</div>
              </div>
              <div className="form-field">
                <label>eBay sale price</label>
                <div className="calc-input-wrap">
                  <span className="calc-input-affix">{selectedMarket?.currencySymbol}</span>
                  <input type="text" inputMode="decimal" placeholder="0.00"
                    value={ebayPriceInput} onChange={numericChange(setEbayPriceInput)} autoComplete="off" />
                </div>
              </div>
              <div className="form-field">
                <label>Amazon postage</label>
                <div className="calc-input-wrap">
                  <span className="calc-input-affix">{selectedMarket?.currencySymbol}</span>
                  <input type="text" inputMode="decimal" placeholder="0.00"
                    value={amazonShipping} onChange={numericChange(setAmazonShipping)} autoComplete="off" />
                </div>
                <div className="field-note">optional</div>
              </div>
            </div>

            <button
              className="calc-btn"
              onClick={handleCalculate}
              disabled={!canCalculate}
            >
              Calculate Profit →
            </button>

            {result && selectedMarket && (
              <div className="results-grid">
                <div className="result-card rc-fee">
                  <div className="rc-label">eBay fees</div>
                  <div className="rc-val">
                    {selectedMarket?.currencySymbol}
                    {result.ebayFees.toFixed(2)}
                  </div>
                  {result.regulatoryFee > 0 && (
                    <div className="rc-sub">
                      incl. {selectedMarket?.currencySymbol}
                      {result.regulatoryFee.toFixed(2)} regulatory
                    </div>
                  )}
                </div>
                {result.vatAmount > 0 && (
                  <div className="result-card rc-fee">
                    <div className="rc-label">VAT on fees</div>
                    <div className="rc-val">
                      {selectedMarket?.currencySymbol}
                      {result.vatAmount.toFixed(2)}
                    </div>
                  </div>
                )}
                <div className="result-card rc-neutral">
                  <div className="rc-label">Amazon cost</div>
                  <div className="rc-val">
                    {selectedMarket?.currencySymbol}
                    {result.amazonCost.toFixed(2)}
                  </div>
                </div>
                <div className="result-card rc-neutral">
                  <div className="rc-label">Gross profit</div>
                  <div className="rc-val">
                    {selectedMarket?.currencySymbol}
                    {result.grossProfit.toFixed(2)}
                  </div>
                </div>
                <div className="result-card rc-neutral">
                  <div className="rc-label">Profit margin</div>
                  <div className="rc-val">
                    {result.profitMargin.toFixed(1)}%
                  </div>
                </div>
                <div className="result-card rc-neutral">
                  <div className="rc-label">Break-even price</div>
                  <div className="rc-val">
                    {selectedMarket?.currencySymbol}
                    {result.breakEven.toFixed(2)}
                  </div>
                </div>
                <div className={`result-card ${result.netProfit >= 0 ? 'rc-net' : 'rc-loss'}`}>
                  <div className="rc-label">Net profit</div>
                  <div className="rc-val">
                    {selectedMarket?.currencySymbol}
                    {result.netProfit.toFixed(2)}
                  </div>
                  <div className="rc-sub">after all fees + costs</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* How It Works */}
        <section className="how-it-works">
          <Container>
            <h2>How Dropshipping Profit Works</h2>
            <div className="steps-grid">
              <div className="step-card">
                <div className="step-num">1</div>
                <h3>Your Costs</h3>
                <p>
                  Add what you pay Amazon for the product plus any Amazon postage. This is the real
                  cost of fulfilling the order before eBay takes its cut.
                </p>
              </div>
              <div className="step-card">
                <div className="step-num">2</div>
                <h3>eBay Fees</h3>
                <p>
                  eBay deducts a Final Value Fee percentage of the sale price plus a fixed order fee.
                  We calculate both for your marketplace and subtract them from your sale price.
                </p>
              </div>
              <div className="step-card">
                <div className="step-num">3</div>
                <h3>Net Profit &amp; Break-even</h3>
                <p>
                  What is left after costs and fees is your net profit. The break-even price shows the
                  minimum you can charge without losing money on the order.
                </p>
              </div>
            </div>
          </Container>
        </section>

        {/* FAQ */}
        <section className="tools-faq">
          <Container>
            <h2>Dropshipping Profit — Frequently Asked Questions</h2>
            <div className="faq-list">
              {FAQ_ITEMS.map((item, idx) => (
                <div className="faq-row" key={idx}>
                  <h3>{item.q}</h3>
                  <p>{item.a}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* RELATED LINKS */}
        <section className="tools-related">
          <Container>
            <h2>Keep exploring</h2>
            <div className="related-links-grid">
              <a href="/blog/is-ebay-dropshipping-profitable" className="related-link-card">
                <span className="rl-label">Guide</span>
                <span className="rl-title">Is eBay Dropshipping Still Profitable?</span>
              </a>
              <a href="/blog/amazon-to-ebay-arbitrage" className="related-link-card">
                <span className="rl-label">Guide</span>
                <span className="rl-title">Amazon to eBay Arbitrage</span>
              </a>
              <a href="/tools/ebay-fee-calculator" className="related-link-card">
                <span className="rl-label">Free Tool</span>
                <span className="rl-title">eBay Fee Calculator</span>
              </a>
              <a href="/compare/coderom-vs-easync" className="related-link-card">
                <span className="rl-label">Compare</span>
                <span className="rl-title">Coderom vs Easync</span>
              </a>
            </div>
          </Container>
        </section>

        {/* CTA */}
        <section className="tools-cta">
          <Container>
            <div className="cta-inner">
              <h2>Start automating your dropshipping</h2>
              <p>Let Coderom monitor Amazon prices and reprice your eBay listings automatically — every margin protected, 24/7.</p>
              <a href={`${APP_URL}/register`} className="cta-btn">Start Free Trial</a>
            </div>
          </Container>
        </section>
      </div>

      <Footer />
      <GuestChatWidget />

      <style>{`
        .hero-breadcrumb { font-size: 12px; color: #94a3b8; margin-bottom: 12px; display: flex; gap: 6px; justify-content: center; flex-wrap: wrap; }
        .hero-breadcrumb a { color: #cbd5e1; text-decoration: none; }
        .hero-breadcrumb a:hover { color: #fb923c; }
        .tools-related { padding: 40px 0; }
        .tools-related h2 { font-size: 24px; font-weight: 700; color: #0f172a; margin-bottom: 18px; }
        .related-links-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; }
        .related-link-card { display: flex; flex-direction: column; gap: 4px; padding: 14px 16px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; text-decoration: none; transition: border-color 0.15s; }
        .related-link-card:hover { border-color: #f97316; }
        .rl-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #f97316; }
        .rl-title { font-size: 13px; font-weight: 500; color: #1e293b; line-height: 1.4; }
        .tools-page { min-height: 100vh; background: #f8fafc; padding: 0; }

        .tools-hero {
          background: linear-gradient(135deg, rgba(30,41,59,0.94) 0%, rgba(51,65,85,0.94) 100%);
          padding: 100px 0 60px;
          color: #fff;
        }
        .tools-hero-content { max-width: 760px; }
        .tools-hero h1 { font-size: 44px; font-weight: 800; margin-bottom: 18px; }
        .tools-hero p { font-size: 16px; line-height: 1.75; opacity: 0.85; }

        .tools-calc-section { padding: 50px 0; }
        .calculator-card {
          max-width: 640px;
          margin: -90px auto 0;
          position: relative;
          background: #fff;
          border-radius: 18px;
          box-shadow: 0 12px 40px rgba(0,0,0,0.12);
          border: 1px solid #e2e8f0;
          padding: 32px;
        }

        .market-tabs { display: flex; gap: 8px; margin-bottom: 1.5rem; flex-wrap: wrap; }
        .market-tab { padding: 8px 18px; border-radius: 999px; border: 1.5px solid #e2e8f0;
          font-size: 14px; font-weight: 500; cursor: pointer; color: #64748b;
          background: #fff; display: flex; align-items: center; gap: 6px;
          transition: all 0.15s; }
        .market-tab.active { background: #f97316; border-color: #f97316; color: #fff; }
        .market-tab:hover:not(.active) { border-color: #f97316; color: #f97316; }
        .calc-wrapper { max-width: 860px; margin: 0 auto; padding: 2rem 1rem; }
        .calc-hero {
          position: relative;
          overflow: hidden;
          border-radius: 0;
          margin-bottom: 2rem;
          padding: 80px 0 60px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .calc-hero-bg {
          position: absolute;
          inset: 0;
          background:
            linear-gradient(135deg,
              rgba(15,23,42,0.72) 0%,
              rgba(15,23,42,0.55) 100%
            ),
            url('/images/tools/hero-profit-calculator.jpg')
            center/cover no-repeat;
        }
        .calc-hero-content {
          position: relative;
          z-index: 1;
          padding: 2.5rem 2rem;
          max-width: 680px;
          width: 100%;
          text-align: center;
        }
        .hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 14px;
          background: rgba(249,115,22,0.2);
          border: 1px solid rgba(249,115,22,0.4);
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          color: #fb923c;
          margin-bottom: 1rem;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
        .calc-hero h1 {
          font-size: 2rem;
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.5rem;
          line-height: 1.3;
        }
        .calc-hero h1 span { color: #fb923c; }
        .calc-hero p {
          font-size: 1rem;
          color: #cbd5e1;
          max-width: 520px;
          margin: 0 auto 1.5rem;
          line-height: 1.6;
        }
        .hero-badges {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: center;
        }
        .hero-badge {
          padding: 4px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2);
          font-size: 13px;
          font-weight: 500;
          color: #e2e8f0;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .hero-badge i { font-size: 12px; color: #fb923c; }
        .calc-card { background: #fff; border: 1px solid #e2e8f0;
          border-radius: 16px; padding: 1.75rem; }
        .section-label { font-size: 11px; font-weight: 600; text-transform: uppercase;
          letter-spacing: 0.07em; color: #94a3b8; margin-bottom: 0.875rem; }
        .form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1.25rem; }
        .form-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 1rem; }
        .form-field { display: flex; flex-direction: column; gap: 5px; }
        .form-field label { font-size: 13px; color: #64748b; font-weight: 500; }
        .calc-input-wrap { display: flex; align-items: center; border: 1px solid #e2e8f0;
          border-radius: 8px; overflow: hidden; background: #fff; }
        .calc-input-wrap:focus-within { border-color: #f97316;
          box-shadow: 0 0 0 3px rgba(249,115,22,0.12); }
        .calc-input-affix { padding: 0 10px; font-size: 14px; color: #94a3b8;
          background: #f8fafc; height: 40px; display: flex; align-items: center;
          border-right: 1px solid #e2e8f0; white-space: nowrap; }
        .calc-input-affix.right { border-right: none; border-left: 1px solid #e2e8f0; }
        .calc-input-wrap input, .calc-input-wrap select { border: none; outline: none;
          padding: 0 12px; height: 40px; font-size: 15px; color: #1e293b;
          background: transparent; width: 100%; }
        .seller-toggle { display: flex; gap: 6px; }
        .seller-btn { flex: 1; padding: 9px; border-radius: 8px;
          border: 1.5px solid #e2e8f0; font-size: 13px; font-weight: 500;
          cursor: pointer; color: #64748b; background: #fff; text-align: center;
          transition: all 0.15s; }
        .seller-btn.active { border-color: #f97316; color: #f97316; background: #fff7ed; }
        .vat-row { display: flex; align-items: center; gap: 10px;
          padding: 10px 0; margin-top: 0.25rem; }
        .vat-row label { font-size: 14px; color: #475569; cursor: pointer; flex: 1; }
        .divider { border: none; border-top: 1px solid #f1f5f9; margin: 1.5rem 0; }
        .field-note { font-size: 11px; color: #94a3b8; margin-top: 3px; }
        .calc-btn { width: 100%; margin-top: 1.5rem; padding: 13px;
          background: linear-gradient(135deg, #f97316, #fb923c);
          color: #fff; border: none; border-radius: 10px; font-size: 15px;
          font-weight: 600; cursor: pointer; display: flex; align-items: center;
          justify-content: center; gap: 8px; transition: opacity 0.15s; }
        .calc-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .calc-btn:not(:disabled):hover { opacity: 0.9; }
        .results-grid { display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 10px; margin-top: 1.5rem; }
        .result-card { border-radius: 10px; padding: 14px 16px; }
        .result-card .rc-label { font-size: 12px; color: #64748b; margin-bottom: 6px; }
        .result-card .rc-val { font-size: 22px; font-weight: 600; color: #1e293b; }
        .result-card .rc-sub { font-size: 11px; color: #94a3b8; margin-top: 3px; }
        .rc-neutral { background: #f8fafc; border: 1px solid #e2e8f0; }
        .rc-fee { background: #fef2f2; border: 1px solid #fecaca; }
        .rc-fee .rc-val { color: #dc2626; }
        .rc-net { background: #f0fdf4; border: 1px solid #bbf7d0; }
        .rc-net .rc-val { color: #16a34a; }
        .rc-profit { background: #eff6ff; border: 1px solid #bfdbfe; }
        .rc-profit .rc-val { color: #2563eb; }
        .rc-loss { background: #fef2f2; border: 1px solid #fecaca; }
        .rc-loss .rc-val { color: #dc2626; }
        @media (max-width: 768px) {
          .form-grid-2, .form-grid-3 { grid-template-columns: 1fr; }
        }

        .fee-input { margin-bottom: 18px; }
        .fee-input label { display: block; font-size: 14px; font-weight: 600; color: #334155; margin-bottom: 8px; }
        .input-with-symbol { position: relative; display: flex; align-items: center; }
        .input-symbol {
          position: absolute; left: 14px; font-size: 18px; font-weight: 700; color: #94a3b8;
        }
        .input-with-symbol input {
          width: 100%;
          padding: 14px 16px 14px 36px;
          font-size: 18px;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .input-with-symbol input:focus {
          border-color: #f97316;
          box-shadow: 0 0 0 3px rgba(249,115,22,0.15);
        }

        .calc-button {
          width: 100%;
          padding: 15px;
          font-size: 16px;
          font-weight: 700;
          color: #fff;
          background: linear-gradient(135deg, #f97316, #fb923c);
          border: none;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 6px;
        }
        .calc-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(249,115,22,0.35);
        }
        .calc-button:disabled { opacity: 0.5; cursor: not-allowed; }

        .result-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-top: 28px;
        }
        .result-item {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .result-item.net-profit { grid-column: 1 / -1; background: #f0fdf4; border-color: #bbf7d0; }
        .result-item.net-loss { grid-column: 1 / -1; background: #fef2f2; border-color: #fecaca; }
        .result-item.net-warn { grid-column: 1 / -1; background: #fffbeb; border-color: #fde68a; }
        .result-label { font-size: 13px; color: #64748b; font-weight: 500; }
        .result-value { font-size: 24px; font-weight: 800; }
        .result-value.neutral { color: #334155; }
        .result-value.fee { color: #dc2626; }
        .result-value.profit { color: #16a34a; }
        .result-value.loss { color: #dc2626; }
        .result-value.warn { color: #d97706; }

        .how-it-works { padding: 70px 0; background: #fff; }
        .how-it-works h2, .tools-faq h2 {
          text-align: center; font-size: 30px; font-weight: 800; color: #1e293b; margin-bottom: 40px;
        }
        .steps-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; max-width: 980px; margin: 0 auto; }
        .step-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 14px; padding: 28px; }
        .step-num {
          width: 40px; height: 40px; border-radius: 10px;
          background: linear-gradient(135deg, #f97316, #fb923c); color: #fff;
          display: flex; align-items: center; justify-content: center;
          font-weight: 800; font-size: 18px; margin-bottom: 16px;
        }
        .step-card h3 { font-size: 18px; font-weight: 700; color: #1e293b; margin-bottom: 10px; }
        .step-card p { font-size: 14px; color: #64748b; line-height: 1.65; margin: 0; }

        .tools-faq { padding: 70px 0; }
        .faq-list { max-width: 800px; margin: 0 auto; }
        .faq-row { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 22px 24px; margin-bottom: 14px; }
        .faq-row h3 { font-size: 16px; font-weight: 700; color: #1e293b; margin-bottom: 8px; }
        .faq-row p { font-size: 14px; color: #64748b; line-height: 1.7; margin: 0; }

        .tools-cta { padding: 80px 0; }
        .cta-inner {
          max-width: 720px; margin: 0 auto; text-align: center;
          background: linear-gradient(135deg, #f97316, #fb923c);
          border-radius: 20px; padding: 56px 32px; color: #fff;
        }
        .cta-inner h2 { font-size: 30px; font-weight: 800; margin-bottom: 12px; }
        .cta-inner p { font-size: 17px; opacity: 0.92; margin-bottom: 28px; }
        .cta-btn {
          display: inline-block; padding: 15px 36px; background: #fff; color: #ea580c;
          font-weight: 700; font-size: 16px; border-radius: 12px; text-decoration: none;
          transition: all 0.2s;
        }
        .cta-btn:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(0,0,0,0.2); color: #ea580c; }

        @media (max-width: 768px) {
          .tools-hero h1 { font-size: 32px; }
          .calculator-card { margin: -70px 16px 0; padding: 24px; }
          .steps-grid { grid-template-columns: 1fr; }
          .result-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default ProfitCalculator;
